<script setup>
/**
 * AjudaBotao — o "Ajuda" da quina superior direita (2026-08-09).
 *
 * Pedido do Gustavo, com a referência (Basepoint) na mão: a barra do topo tinha o caminho à
 * esquerda e nada à direita. Lá existem dois controles nessa quina — "Configure" e "Help" —
 * e ele foi explícito sobre qual queria: **só o Help.**
 *
 * A decisão de não trazer o "Configure" junto está certa e vale registrar: configuração
 * neste produto é por ASSUNTO e já mora onde o assunto vive (a engrenagem do board configura
 * a trilha, a aba Fluxos configura a automação, Configurações cuida da conta). Um botão
 * genérico na quina seria uma quarta porta para o mesmo lugar, e a pessoa teria de adivinhar
 * qual das quatro abre o que ela quer.
 *
 * ── POR QUE ELE VIVE DENTRO DO TelaHeader ───────────────────────────────────────────────
 * Ajuda que aparece numa tela e some na seguinte é pior que não ter ajuda: quem precisou uma
 * vez volta a procurar no mesmo canto e não encontra. Está no componente da barra, então
 * nasce em toda tela que usa a barra — sem cada tela precisar lembrar de montá-lo.
 *
 * ── O QUE ELE FAZ, E O QUE ELE NÃO FINGE FAZER ──────────────────────────────────────────
 * Ele NÃO abre uma central de artigos: o Help Center do Chatwoot está desligado nesta conta,
 * e um botão que leva a uma base vazia é pior que nenhum botão. O que ele oferece são os
 * dois caminhos que EXISTEM e resolvem de verdade — perguntar à assistente e chamar a
 * equipe — mais a versão do sistema, que é a primeira coisa que se pergunta a quem relata
 * um problema.
 *
 * pt-BR locked.
 */
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

const route = useRoute();
const router = useRouter();
const globalConfig = useMapGetter('globalConfig/get');

const aberto = ref(false);
const accountId = computed(() => route.params.accountId);
const versao = computed(() => globalConfig.value?.appVersion || null);

function ir(nome) {
  aberto.value = false;
  router.push({ name: nome, params: { accountId: accountId.value } });
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) -->
  <div v-on-clickaway="() => (aberto = false)" class="relative shrink-0">
    <!-- ⚠️ 16 → 14px e ícone 20 → 18px (2026-08-11). Ele pediu a quina "harmônica" com o
         resto, e harmonia aqui é medida: a migalha à esquerda e o menu da barra foram os dois
         para 14px, então a palavra da quina oposta tem de ser a mesma — se ela ficar um
         degrau acima, "Ajuda" passa a ser a maior palavra da faixa, e o item que MENOS
         pertence à tela vira o que mais chama.
         O desenho desce para 18 porque a tinta dele é `slate-11`, bem mais escura que a
         `slate-9` da casinha: no mesmo tamanho ele pesaria o dobro dela. Tamanho e tom se
         compensam — é a razão de a casinha poder ficar em 20 e este não.
         `leading-5` fica (caixa de 20px), e o `size-5` no wrapper mantém o alinhamento
         mesmo com o desenho menor dentro. `pl-2 pr-2.5` são explícitos, então este botão
         nunca sofreu o esmagamento do `_base.scss` — ver a nota no Sidebar.vue. -->
    <button
      type="button"
      class="h-8 pl-2 pr-2.5 inline-flex items-center gap-1.5 rounded-lg text-[14px] leading-5 font-400 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-12"
      :aria-expanded="aberto"
      aria-label="Ajuda"
      @click="aberto = !aberto"
    >
      <span class="size-5 shrink-0 inline-flex items-center justify-center">
        <span class="i-lucide-circle-help size-[18px] shrink-0" />
      </span>
      <span class="hidden sm:inline">Ajuda</span>
    </button>

    <Transition
      enter-active-class="transition-[transform,opacity] duration-mj-toque ease-mj motion-reduce:transition-opacity motion-reduce:transform-none"
      enter-from-class="-translate-y-1 scale-[0.96] opacity-0 motion-reduce:translate-y-0 motion-reduce:scale-100"
      leave-active-class="transition-[transform,opacity] duration-mj-toque ease-mj motion-reduce:transition-opacity motion-reduce:transform-none"
      leave-to-class="-translate-y-1 scale-[0.98] opacity-0 motion-reduce:translate-y-0 motion-reduce:scale-100"
    >
      <div
        v-if="aberto"
        class="absolute right-0 top-full z-50 mt-1.5 w-[min(264px,calc(100vw-24px))] origin-top-right rounded-card border border-n-weak bg-n-background py-1.5 shadow-mj-4"
        @click.stop
      >
        <button
          type="button"
          class="w-full px-3 py-2 flex items-start gap-2.5 text-left hover:bg-n-alpha-1 transition-colors duration-mj-toque"
          @click="ir('crm_assistente')"
        >
          <span
            class="i-lucide-sparkles size-4 shrink-0 mt-0.5 text-n-slate-10"
          />
          <span class="flex flex-col gap-0.5 min-w-0">
            <span class="text-mj-13 font-500 text-n-slate-12">
              Perguntar à assistente
            </span>
            <span class="text-mj-11 text-n-slate-10">
              Dúvidas sobre a operação e os números.
            </span>
          </span>
        </button>

        <button
          type="button"
          class="w-full px-3 py-2 flex items-start gap-2.5 text-left hover:bg-n-alpha-1 transition-colors duration-mj-toque"
          @click="ir('crm_internal_inbox')"
        >
          <span
            class="i-lucide-users-round size-4 shrink-0 mt-0.5 text-n-slate-10"
          />
          <span class="flex flex-col gap-0.5 min-w-0">
            <span class="text-mj-13 font-500 text-n-slate-12">
              Falar com a equipe
            </span>
            <span class="text-mj-11 text-n-slate-10">
              Abre o canal interno para pedir ajuda a alguém.
            </span>
          </span>
        </button>

        <!-- A versão. Parece detalhe e é o contrário: é a primeira pergunta de quem vai
             investigar um problema relatado, e sem ela a resposta é sempre "não sei". -->
        <template v-if="versao">
          <div class="my-1.5 h-px bg-n-weak" />
          <p class="px-3 pb-0.5 text-mj-11 text-n-slate-10 tabular-nums">
            Versão {{ versao }}
          </p>
        </template>
      </div>
    </Transition>
  </div>
</template>
